class SpeciesUpvoteSystem {
    constructor(supabaseUrl, supabaseKey) {
        // Initialize Supabase client
        this.supabase = window.supabase.createClient(supabaseUrl, supabaseKey);
        this.birdSpecies = [];
        this.currentVotes = new Map();
        this.lastVoteTime = this.getLastVoteTime();
        this.countdownInterval = null;

        this.init();
    }

    async init() {
        try {
            // Load bird species list
            await this.loadBirdSpecies();

            // Load current vote counts
            await this.loadVoteCounts();

            // Render the UI
            this.renderUI();

            // Set up real-time subscriptions
            this.subscribeToVoteUpdates();
        } catch (error) {
            console.error('Error initializing species upvote system:', error);
        }
    }

    async loadBirdSpecies() {
        try {
            // Use the same URL pattern as other theme assets
            // Extract the base asset URL from a known working asset (like the current CSS)
            const stylesheets = Array.from(document.styleSheets);
            let assetBaseUrl = window.Shopify.routes.root + 'assets/';

            // Try to find the CDN URL pattern from existing assets
            for (const stylesheet of stylesheets) {
                if (stylesheet.href && stylesheet.href.includes('/assets/species-upvote.css')) {
                    const basePattern = stylesheet.href.replace('species-upvote.css', '').replace(/\?v=.*$/, '');
                    assetBaseUrl = basePattern;
                    break;
                }
            }

            const response = await fetch(assetBaseUrl + 'north-american-birds.json');
            this.birdSpecies = await response.json();
        } catch (error) {
            console.error('Error loading bird species:', error);
            // Fallback list for testing
            this.birdSpecies = [
                "American Robin", "Blue Jay", "Cardinal", "Crow", "Eagle",
                "Finch", "Hawk", "Owl", "Robin", "Sparrow", "Woodpecker"
            ];
        }
    }

    async loadVoteCounts() {
        try {
            const { data, error } = await this.supabase
                .from('species_requests')
                .select('species_name, vote_count')
                .order('vote_count', { ascending: false });

            if (error) throw error;

            // Convert to Map for easy lookup
            this.currentVotes = new Map();
            data.forEach(row => {
                this.currentVotes.set(row.species_name, row.vote_count);
            });
        } catch (error) {
            console.error('Error loading vote counts:', error);
        }
    }

    async upvoteSpecies(speciesName) {
        // Check rate limiting
        if (!this.canVote()) {
            return;
        }

        try {
            // Optimistic update
            const currentCount = this.currentVotes.get(speciesName) || 0;
            this.currentVotes.set(speciesName, currentCount + 1);

            // Update database
            const { data, error } = await this.supabase.rpc('increment_species_vote', {
                species_name_param: speciesName
            });

            if (error) throw error;

            // Update localStorage to prevent multiple votes
            this.setLastVoteTime();

            // Re-render entire UI to show new vote state and cooldown
            this.renderUI();

        } catch (error) {
            console.error('Error upvoting species:', error);
            // Revert optimistic update
            const currentCount = this.currentVotes.get(speciesName) || 0;
            this.currentVotes.set(speciesName, Math.max(0, currentCount - 1));
            this.renderUI();
        }
    }

    subscribeToVoteUpdates() {
        // Subscribe to real-time updates from Supabase
        this.supabase
            .channel('species_votes')
            .on(
                'postgres_changes',
                {
                    event: '*',
                    schema: 'public',
                    table: 'species_requests'
                },
                (payload) => {
                    // Update local vote counts when database changes
                    this.currentVotes.set(payload.new.species_name, payload.new.vote_count);
                    this.renderUI();
                }
            )
            .subscribe();
    }

    canVote() {
        const twentyFourHours = 24 * 60 * 60 * 1000;
        return Date.now() - this.lastVoteTime > twentyFourHours;
    }

    getLastVoteTime() {
        const stored = localStorage.getItem('colorflock_last_vote');
        return stored ? parseInt(stored) : 0;
    }

    setLastVoteTime() {
        localStorage.setItem('colorflock_last_vote', Date.now().toString());
        this.lastVoteTime = Date.now();
    }

    getTopSpecies(limit = 10) {
        return Array.from(this.currentVotes.entries())
            .sort((a, b) => b[1] - a[1])
            .slice(0, limit);
    }

    filterSpecies(searchTerm) {
        if (!searchTerm) return [];
        return this.birdSpecies.filter(species =>
            species.toLowerCase().includes(searchTerm.toLowerCase())
        ).slice(0, 10); // Limit search results
    }

    renderUI() {
        const container = document.getElementById('species-upvote-container');
        if (!container) return;

        const canVote = this.canVote();
        const timeUntilVote = canVote ? 0 : this.getTimeUntilNextVote();

        container.innerHTML = `
      <div class="species-upvote-system">
        <h2>Vote for New Bird Species!</h2>
        <p>Help us prioritize which birds to add next. Vote for your favorites and see what the community wants most.</p>
        
        ${!canVote ? `
          <div class="vote-cooldown">
            <p>⏱️ You can vote again in <span id="countdown-timer">${this.formatTimeRemaining(timeUntilVote)}</span></p>
          </div>
        ` : ''}
        
        <div class="search-section">
          <h3>🔍 Search & Vote</h3>
          <div class="search-container">
            <input 
              type="text" 
              id="species-search" 
              placeholder="Search for a bird species..."
              autocomplete="off"
            >
            <div id="search-results" class="search-results"></div>
            <div id="popular-suggestions" class="search-results" style="display: none;">
              <div class="search-header">Other popular birds:</div>
            </div>
          </div>
        </div>
        
        <div class="top-species">
          <h3>🏆 Most Requested Species</h3>
          <div id="top-species-list">
            ${this.renderTopSpecies()}
          </div>
          <div class="mailing-list-cta">
            <h3>🚨 Get new bird alerts!</h3>
            <p style="text-align: left;">Want to get notified when we release new birds? Sign up for our mailing list below (and get $5 off your first order).</p>
          </div>
        </div>
      </div>
    `;

        this.setupEventListeners();

        // Start countdown timer if needed
        if (!canVote) {
            this.startCountdownTimer();
        }
    }

    setupEventListeners() {
        const searchInput = document.getElementById('species-search');
        const searchResults = document.getElementById('search-results');
        const popularSuggestions = document.getElementById('popular-suggestions');

        // Global event listener for all upvote buttons
        document.addEventListener('click', (e) => {
            if (e.target.classList.contains('upvote-btn') && !e.target.disabled) {
                const species = e.target.dataset.species;
                if (species && this.canVote()) {
                    this.upvoteSpecies(species);
                }
            }
        });

        if (searchInput) {
            // Show popular suggestions when input gets focus
            searchInput.addEventListener('focus', () => {
                if (!searchInput.value.trim()) {
                    this.showPopularSuggestions();
                }
            });

            // Hide suggestions when input loses focus (with small delay)
            searchInput.addEventListener('blur', () => {
                setTimeout(() => {
                    if (popularSuggestions) {
                        popularSuggestions.style.display = 'none';
                    }
                    if (searchResults) {
                        searchResults.style.display = 'none';
                    }
                }, 200); // Delay to allow clicking on results
            });

            // Search as user types
            searchInput.addEventListener('input', () => {
                const query = searchInput.value.trim();
                if (query.length > 0) {
                    this.performSearch(query);
                    if (popularSuggestions) {
                        popularSuggestions.style.display = 'none';
                    }
                } else {
                    this.showPopularSuggestions();
                    if (searchResults) {
                        searchResults.style.display = 'none';
                    }
                }
            });
        }
    }

    showPopularSuggestions() {
        const popularSuggestions = document.getElementById('popular-suggestions');
        const searchResults = document.getElementById('search-results');

        if (!popularSuggestions) return;

        // Hide search results
        if (searchResults) {
            searchResults.style.display = 'none';
        }

        // Get birds ranked 11-20
        const topSpecies = this.getTopSpecies(20); // Get top 20
        const suggestions = topSpecies.slice(10, 20); // Take ranks 11-20

        if (suggestions.length === 0) {
            popularSuggestions.innerHTML = `
                <div class="search-header">No popular suggestions yet</div>
                <div class="search-item">Be the first to vote for a species!</div>
            `;
        } else {
            popularSuggestions.innerHTML = `
                <div class="search-header">Other popular birds:</div>
                ${suggestions.map(([species, count], index) => `
                    <div class="search-item" data-species="${species}">
                        <span class="species-rank">#${11 + index}</span>
                        <span class="species-name">${species}</span>
                        <span class="species-votes">${count} vote${count !== 1 ? 's' : ''}</span>
                        <button class="vote-btn ${this.canVote() ? '' : 'disabled'}" 
                                data-species="${species}"
                                ${this.canVote() ? '' : 'disabled'}>
                            ${this.canVote() ? '👍 Vote' : 'Voted'}
                        </button>
                    </div>
                `).join('')}
            `;

            // Add click handlers for vote buttons
            popularSuggestions.querySelectorAll('.vote-btn').forEach(btn => {
                btn.addEventListener('click', (e) => {
                    e.stopPropagation();
                    if (this.canVote()) {
                        const species = btn.dataset.species;
                        this.upvoteSpecies(species); // Changed from vote to upvoteSpecies
                    }
                });
            });

            // Add click handlers for selecting species
            popularSuggestions.querySelectorAll('.search-item').forEach(item => {
                item.addEventListener('click', () => {
                    const species = item.dataset.species;
                    const searchInput = document.getElementById('species-search');
                    if (searchInput) {
                        searchInput.value = species;
                        searchInput.focus();
                    }
                });
            });
        }

        popularSuggestions.style.display = 'block';
    }

    renderTopSpecies() {
        const topSpecies = this.getTopSpecies();
        if (topSpecies.length === 0) {
            return '<p>No votes yet! Be the first to vote for a species.</p>';
        }

        return topSpecies.map(([species, votes], index) => `
      <div class="species-item top-species-item">
        <span class="species-rank">#${index + 1}</span>
        <span class="species-name">${species}</span>
        <span class="species-votes">${votes} ${votes === 1 ? 'vote' : 'votes'}</span>
        <button 
          class="upvote-btn" 
          data-species="${species}"
          ${!this.canVote() ? 'disabled' : ''}
        >
          👍 ${this.canVote() ? 'Vote' : 'Voted'}
        </button>
      </div>
    `).join('');
    }

    renderSearchResults(results) {
        const container = document.getElementById('search-results');
        if (!container) return;

        if (results.length === 0) {
            container.innerHTML = '';
            return;
        }

        container.innerHTML = `
      <div class="search-results-list">
        ${results.map(species => `
          <div class="species-item search-result-item">
            <span class="species-name">${species}</span>
            <span class="species-votes">${this.currentVotes.get(species) || 0} votes</span>
            <button 
              class="upvote-btn" 
              data-species="${species}"
              ${!this.canVote() ? 'disabled' : ''}
            >
              👍 ${this.canVote() ? 'Vote' : 'Voted'}
            </button>
          </div>
        `).join('')}
      </div>
    `;
    }

    performSearch(query) {
        const searchResults = document.getElementById('search-results');
        const popularSuggestions = document.getElementById('popular-suggestions');

        if (!searchResults) return;

        // Hide popular suggestions when searching
        if (popularSuggestions) {
            popularSuggestions.style.display = 'none';
        }

        // Filter bird species based on query
        const matches = this.birdSpecies.filter(species =>
            species.toLowerCase().includes(query.toLowerCase())
        ).slice(0, 10); // Limit to 10 results

        if (matches.length === 0) {
            searchResults.innerHTML = `
                <div class="search-header">No birds found matching "${query}"</div>
                <div class="search-item">Try a different search term</div>
            `;
        } else {
            searchResults.innerHTML = `
                <div class="search-header">Found ${matches.length} bird${matches.length !== 1 ? 's' : ''} matching "${query}":</div>
                ${matches.map(species => {
                const currentVotes = this.currentVotes.get(species) || 0;
                return `
                        <div class="search-item" data-species="${species}">
                            <span class="species-name">${species}</span>
                            <span class="species-votes">${currentVotes} vote${currentVotes !== 1 ? 's' : ''}</span>
                            <button class="vote-btn ${this.canVote() ? '' : 'disabled'}" 
                                    data-species="${species}"
                                    ${this.canVote() ? '' : 'disabled'}>
                                ${this.canVote() ? '👍 Vote' : 'Voted'}
                            </button>
                        </div>
                    `;
            }).join('')}
            `;

            // Add click handlers for vote buttons
            searchResults.querySelectorAll('.vote-btn').forEach(btn => {
                btn.addEventListener('click', (e) => {
                    e.stopPropagation();
                    if (this.canVote()) {
                        const species = btn.dataset.species;
                        this.upvoteSpecies(species);
                    }
                });
            });

            // Add click handlers for selecting species
            searchResults.querySelectorAll('.search-item').forEach(item => {
                item.addEventListener('click', () => {
                    const species = item.dataset.species;
                    const searchInput = document.getElementById('species-search');
                    if (searchInput) {
                        searchInput.value = species;
                    }
                });
            });
        }

        searchResults.style.display = 'block';
    }

    startCountdownTimer() {
        // Clear any existing timer
        if (this.countdownInterval) {
            clearInterval(this.countdownInterval);
        }

        this.countdownInterval = setInterval(() => {
            const timeUntilVote = this.getTimeUntilNextVote();
            const timerElement = document.getElementById('countdown-timer');

            if (timeUntilVote <= 0) {
                // Time's up! User can vote again
                clearInterval(this.countdownInterval);
                this.renderUI(); // Re-render to remove countdown and enable voting
            } else if (timerElement) {
                // Update the countdown display
                timerElement.textContent = this.formatTimeRemaining(timeUntilVote);
            }
        }, 60000); // Update every minute
    }



    getTimeUntilNextVote() {
        const twentyFourHours = 24 * 60 * 60 * 1000;
        return twentyFourHours - (Date.now() - this.lastVoteTime);
    }

    formatTimeRemaining(milliseconds) {
        const hours = Math.floor(milliseconds / (60 * 60 * 1000));
        const minutes = Math.floor((milliseconds % (60 * 60 * 1000)) / (60 * 1000));

        if (hours > 0) {
            return `${hours}h ${minutes}m`;
        }
        return `${minutes}m`;
    }



    cleanup() {
        // Clear countdown timer to prevent memory leaks
        if (this.countdownInterval) {
            clearInterval(this.countdownInterval);
            this.countdownInterval = null;
        }
    }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
    // These values should be set in your theme's settings or hardcoded
    const SUPABASE_URL = window.COLORFLOCK_SUPABASE_URL || 'YOUR_SUPABASE_URL';
    const SUPABASE_ANON_KEY = window.COLORFLOCK_SUPABASE_KEY || 'YOUR_SUPABASE_ANON_KEY';

    if (document.getElementById('species-upvote-container')) {
        const speciesSystem = new SpeciesUpvoteSystem(SUPABASE_URL, SUPABASE_ANON_KEY);

        // Cleanup on page unload
        window.addEventListener('beforeunload', () => {
            speciesSystem.cleanup();
        });
    }
}); 